import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GlassCard } from "../components/GlassCard";
import { Shield, Trash2, UserPlus, Download, DatabaseBackup, Sparkles, Eraser, Pencil, Check, X } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";

export default function Permissions() {
  const { mode, switchMode } = useAuth();
  const [perms, setPerms] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [email, setEmail] = useState("");
  const [level, setLevel] = useState("view");
  const [scope, setScope] = useState("all"); // "all" or "some"
  const [selectedIds, setSelectedIds] = useState([]);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [snapshots, setSnapshots] = useState([]);

  // Inline edit state for an existing permission. `editingPerm` holds the id
  // of the row being edited (or null). `editDraft` is the staged changes.
  const [editingPerm, setEditingPerm] = useState(null);
  const [editDraft, setEditDraft] = useState({ level: "view", scope: "all", account_ids: [] });

  const startEditPerm = (p) => {
    setEditingPerm(p.id);
    setEditDraft({
      level: p.level,
      scope: p.account_ids?.length ? "some" : "all",
      account_ids: [...(p.account_ids || [])],
    });
  };

  const cancelEditPerm = () => {
    setEditingPerm(null);
  };

  const toggleEditAccount = (id) => {
    setEditDraft((d) => ({
      ...d,
      account_ids: d.account_ids.includes(id)
        ? d.account_ids.filter((x) => x !== id)
        : [...d.account_ids, id],
    }));
  };

  const saveEditPerm = async (p) => {
    try {
      await api.patch(`/permissions/${p.id}`, {
        grantee_email: p.grantee_email,  // ignored server-side, kept for payload shape
        level: editDraft.level,
        account_ids: editDraft.scope === "all" ? [] : editDraft.account_ids,
      });
      setMsg({ kind: "ok", text: `Updated access for ${p.grantee_email}` });
      setEditingPerm(null);
      refresh();
    } catch (err) {
      setMsg({
        kind: "err",
        text: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    }
  };

  const refresh = useCallback(async () => {
    const [p, a, s] = await Promise.all([
      api.get("/permissions/granted"),
      api.get("/accounts"),
      api.get("/backup/history").catch(() => ({ data: { snapshots: [] } })),
    ]);
    setPerms(p.data.permissions || []);
    setAccounts(a.data.accounts || []);
    setSnapshots(s.data.snapshots || []);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const toggleAccount = (id) => {
    setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const grant = async (e) => {
    e.preventDefault();
    setMsg(null);
    setBusy(true);
    try {
      await api.post("/permissions", {
        grantee_email: email.trim().toLowerCase(),
        level,
        account_ids: scope === "all" ? [] : selectedIds,
      });
      setMsg({ kind: "ok", text: `Access granted to ${email}` });
      setEmail("");
      setSelectedIds([]);
      setScope("all");
      setLevel("view");
      refresh();
    } catch (err) {
      setMsg({
        kind: "err",
        text: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    if (!confirm("Revoke this access?")) return;
    await api.delete(`/permissions/${id}`);
    refresh();
  };

  const downloadBackup = async () => {
    const token = localStorage.getItem("access_token");
    const resp = await fetch(`${API}/backup/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ledger-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
  };

  const triggerSnapshot = async () => {
    setBusy(true);
    try {
      await api.post("/backup/snapshot-now");
      setMsg({ kind: "ok", text: "Snapshot saved to server." });
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const seedDemo = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const { data } = await api.post("/admin/seed-demo");
      setMsg({
        kind: "ok",
        text: `Demo data seeded: ${data.accounts} accounts, ${data.entries} entries. Switch to Demo mode to view.`,
      });
      if (mode !== "demo") switchMode("demo");
    } catch (err) {
      setMsg({
        kind: "err",
        text: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setBusy(false);
    }
  };

  const clearDemo = async () => {
    if (!confirm("Clear all demo data? (Live data is untouched.)")) return;
    setBusy(true);
    setMsg(null);
    try {
      const { data } = await api.post("/admin/clear-demo");
      setMsg({
        kind: "ok",
        text: `Demo cleared: ${data.accounts_deleted} accounts, ${data.entries_deleted} entries removed.`,
      });
      window.dispatchEvent(new Event("workspace-changed"));
    } finally {
      setBusy(false);
    }
  };


  return (
    <div className="space-y-8" data-testid="permissions-page">
      <div>
        <p className="text-stone-500">Sharing & security</p>
        <h1 className="font-serif text-5xl">Permissions</h1>
      </div>

      {/* Grant new */}
      <GlassCard>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-2xl glass-gold flex items-center justify-center">
            <UserPlus size={18} className="text-[color:var(--gold-deep)]" />
          </div>
          <h3 className="font-serif text-2xl">Share access</h3>
        </div>
        <form onSubmit={grant} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label-tiny">Grantee email</label>
              <input
                type="email"
                required
                data-testid="grantee-email-input"
                className="w-full mt-1 px-4 py-3 rounded-2xl bg-white/70 border border-white/80 focus:bg-white outline-none focus:ring-2 focus:ring-[color:var(--gold)]/40"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
            <div>
              <label className="label-tiny">Access level</label>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger
                  className="w-full mt-1 rounded-2xl bg-white/70"
                  data-testid="level-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass-strong rounded-xl">
                  <SelectItem value="view">View only</SelectItem>
                  <SelectItem value="edit">View + edit</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="label-tiny">Account scope</label>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => setScope("all")}
                className={`px-4 py-2 rounded-full text-sm ${scope === "all" ? "bg-[color:var(--gold)] text-white" : "bg-white/70"}`}
                data-testid="scope-all-btn"
              >
                All accounts
              </button>
              <button
                type="button"
                onClick={() => setScope("some")}
                className={`px-4 py-2 rounded-full text-sm ${scope === "some" ? "bg-[color:var(--gold)] text-white" : "bg-white/70"}`}
                data-testid="scope-some-btn"
              >
                Specific accounts
              </button>
            </div>
          </div>
          {scope === "some" && (
            <div className="glass rounded-2xl p-4 max-h-64 overflow-auto space-y-2">
              {accounts.map((a) => (
                <label
                  key={a.id}
                  className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-white/60 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedIds.includes(a.id)}
                    onCheckedChange={() => toggleAccount(a.id)}
                    data-testid={`scope-acct-${a.id}`}
                  />
                  <span className="text-sm">{a.name}</span>
                </label>
              ))}
              {accounts.length === 0 && (
                <div className="text-sm text-stone-500 text-center py-4">No accounts yet</div>
              )}
            </div>
          )}

          {msg && (
            <div
              className={`rounded-xl px-3 py-2 text-sm ${msg.kind === "ok" ? "bg-emerald-50/70 text-emerald-700" : "bg-red-50/70 text-red-700"}`}
            >
              {msg.text}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !email.trim() || (scope === "some" && selectedIds.length === 0)}
            className="btn-gold"
            data-testid="grant-submit-btn"
          >
            Grant access
          </button>
        </form>
      </GlassCard>

      {/* Existing */}
      <div>
        <h3 className="font-serif text-2xl mb-4">Existing shares</h3>
        {perms.length === 0 ? (
          <GlassCard className="text-center text-stone-500 py-10">
            No permissions granted yet.
          </GlassCard>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {perms.map((p) => (
              <GlassCard key={p.id} className="p-5">
                {editingPerm === p.id ? (
                  <div className="space-y-3" data-testid={`perm-edit-${p.id}`}>
                    <div>
                      <div className="font-medium">{p.grantee_name || p.grantee_email}</div>
                      <div className="text-xs text-stone-500">{p.grantee_email}</div>
                    </div>
                    <div>
                      <label className="label-tiny">Access level</label>
                      <div className="flex gap-2 mt-1">
                        <button
                          type="button"
                          onClick={() => setEditDraft((d) => ({ ...d, level: "view" }))}
                          className={`px-3 py-1.5 rounded-full text-xs ${editDraft.level === "view" ? "bg-[color:var(--gold)] text-white" : "bg-white/70"}`}
                          data-testid={`perm-edit-level-view-${p.id}`}
                        >
                          View only
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditDraft((d) => ({ ...d, level: "edit" }))}
                          className={`px-3 py-1.5 rounded-full text-xs ${editDraft.level === "edit" ? "bg-[color:var(--gold)] text-white" : "bg-white/70"}`}
                          data-testid={`perm-edit-level-edit-${p.id}`}
                        >
                          View + Edit
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="label-tiny">Account scope</label>
                      <div className="flex gap-2 mt-1">
                        <button
                          type="button"
                          onClick={() => setEditDraft((d) => ({ ...d, scope: "all" }))}
                          className={`px-3 py-1.5 rounded-full text-xs ${editDraft.scope === "all" ? "bg-[color:var(--gold)] text-white" : "bg-white/70"}`}
                          data-testid={`perm-edit-scope-all-${p.id}`}
                        >
                          All accounts
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditDraft((d) => ({ ...d, scope: "some" }))}
                          className={`px-3 py-1.5 rounded-full text-xs ${editDraft.scope === "some" ? "bg-[color:var(--gold)] text-white" : "bg-white/70"}`}
                          data-testid={`perm-edit-scope-some-${p.id}`}
                        >
                          Specific
                        </button>
                      </div>
                    </div>
                    {editDraft.scope === "some" && (
                      <div className="glass rounded-xl p-3 max-h-48 overflow-auto space-y-1">
                        {accounts.map((a) => (
                          <label
                            key={a.id}
                            className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-white/60 cursor-pointer"
                          >
                            <Checkbox
                              checked={editDraft.account_ids.includes(a.id)}
                              onCheckedChange={() => toggleEditAccount(a.id)}
                              data-testid={`perm-edit-acct-${p.id}-${a.id}`}
                            />
                            <span className="text-sm">{a.name}</span>
                          </label>
                        ))}
                        {accounts.length === 0 && (
                          <div className="text-xs text-stone-500 text-center py-2">No accounts</div>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-end gap-2 pt-1">
                      <button
                        onClick={cancelEditPerm}
                        className="btn-ghost btn-ghost-sm flex items-center gap-1"
                        data-testid={`perm-edit-cancel-${p.id}`}
                      >
                        <X size={12} /> Cancel
                      </button>
                      <button
                        onClick={() => saveEditPerm(p)}
                        disabled={editDraft.scope === "some" && editDraft.account_ids.length === 0}
                        className="btn-gold flex items-center gap-1 text-xs px-3 py-1.5"
                        data-testid={`perm-edit-save-${p.id}`}
                      >
                        <Check size={12} /> Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium">{p.grantee_name || p.grantee_email}</div>
                      <div className="text-xs text-stone-500">{p.grantee_email}</div>
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${p.level === "edit" ? "bg-amber-100 text-amber-800" : "bg-stone-100 text-stone-600"}`}
                        >
                          {p.level === "edit" ? "View + Edit" : "View only"}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">
                          {p.account_ids?.length
                            ? `${p.account_ids.length} accounts`
                            : "All accounts"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => startEditPerm(p)}
                        className="w-8 h-8 rounded-full hover:bg-white/60 text-stone-600 inline-flex items-center justify-center"
                        data-testid={`edit-perm-${p.id}`}
                        title="Edit access"
                      >
                        <Pencil size={14} strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={() => revoke(p.id)}
                        className="w-8 h-8 rounded-full hover:bg-red-50 text-red-600 inline-flex items-center justify-center"
                        data-testid={`revoke-${p.id}`}
                        title="Revoke"
                      >
                        <Trash2 size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                )}
              </GlassCard>
            ))}
          </div>
        )}
      </div>

      {/* Demo data seeding */}
      <div>
        <h3 className="font-serif text-2xl mb-4 flex items-center gap-2">
          <Sparkles size={20} className="text-[color:var(--gold-deep)]" />
          Demo mode
        </h3>
        <GlassCard>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="max-w-xl">
              <p className="text-sm text-stone-600 leading-relaxed">
                Demo mode gives you a completely isolated playground with sample accounts and
                entries. Nothing in Demo mode affects your Live data — ever. Switch modes anytime
                using the pill in the top bar.
              </p>
              <p className="text-xs text-stone-500 mt-2">
                Current mode:{" "}
                <span
                  className={`font-medium ${mode === "demo" ? "text-[color:var(--gold-deep)]" : "text-emerald-700"}`}
                  data-testid="current-mode-label"
                >
                  {mode.toUpperCase()}
                </span>
              </p>
            </div>
            <div className="flex flex-col gap-2 min-w-[220px]">
              {mode !== "demo" && (
                <button
                  onClick={() => switchMode("demo")}
                  className="btn-ghost flex items-center justify-center gap-2 text-sm"
                  data-testid="enter-demo-btn"
                >
                  Enter demo mode
                </button>
              )}
              <button
                onClick={seedDemo}
                disabled={busy}
                className="btn-gold flex items-center justify-center gap-2 text-sm"
                data-testid="seed-demo-btn"
              >
                <Sparkles size={14} /> Seed demo data
              </button>
              <button
                onClick={clearDemo}
                disabled={busy}
                className="btn-ghost flex items-center justify-center gap-2 text-sm text-rose-600"
                data-testid="clear-demo-btn"
              >
                <Eraser size={14} /> Clear demo data
              </button>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Backups */}
      <div>
        <h3 className="font-serif text-2xl mb-4 flex items-center gap-2">
          <Shield size={20} className="text-[color:var(--gold-deep)]" />
          Data safety
        </h3>
        <GlassCard>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="label-tiny">Immutability</div>
              <p className="text-sm text-stone-600 mt-1">
                Entries and accounts can never be deleted. Entries stop being modifiable once
                finalized.
              </p>
              <div className="label-tiny mt-4">Automatic snapshots</div>
              <p className="text-sm text-stone-600 mt-1">
                Every 24 hours the server writes a full JSON snapshot of your workspace to
                <code className="mx-1 text-xs bg-white/80 px-1.5 py-0.5 rounded">/app/backups/</code>
                on disk.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={downloadBackup}
                className="btn-gold flex items-center justify-center gap-2"
                data-testid="backup-download-btn"
              >
                <Download size={16} /> Download JSON backup
              </button>
              <button
                onClick={triggerSnapshot}
                disabled={busy}
                className="btn-ghost flex items-center justify-center gap-2"
                data-testid="backup-snapshot-btn"
              >
                <DatabaseBackup size={16} /> Snapshot now
              </button>
              <div className="text-xs text-stone-500 mt-2">
                {snapshots.length > 0
                  ? `${snapshots.length} snapshots on server. Latest: ${snapshots[0].date}`
                  : "No snapshots yet."}
              </div>
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
